import { type ReactNode } from 'react';
import { Inbox } from 'lucide-react';
import { cn } from './cn';

export interface EmptyStateProps {
  /** Icon to display. Defaults to InboxIcon. */
  icon?: ReactNode;
  title: string;
  description?: string;
  /** Optional CTA button or any ReactNode rendered below the description. */
  action?: ReactNode;
  /** `inline` is a compact one-line message for dropdowns, pickers and small lists (no card/icon). */
  size?: 'inline' | 'sm' | 'md' | 'lg';
}

const SIZE_CLASSES = {
  sm: {
    wrapper: 'py-8 px-4',
    iconBox: 'h-8 w-8',
    icon: 'h-4 w-4',
    title: 'text-sm',
    description: 'text-xs',
  },
  md: {
    wrapper: 'py-12 px-6',
    iconBox: 'h-10 w-10',
    icon: 'h-5 w-5',
    title: 'text-sm',
    description: 'text-xs',
  },
  lg: {
    wrapper: 'py-16 px-8',
    iconBox: 'h-12 w-12',
    icon: 'h-6 w-6',
    title: 'text-base',
    description: 'text-sm',
  },
} as const;

export function EmptyState({
  icon,
  title,
  description,
  action,
  size = 'md',
}: EmptyStateProps) {
  if (size === 'inline') {
    return (
      <p className="px-3 py-2 text-[11px] text-text-tertiary">
        {title}
        {description && ` ${description}`}
        {action && <> {action}</>}
      </p>
    );
  }

  const s = SIZE_CLASSES[size];

  return (
    <div
      className={cn(
        'flex flex-col items-center gap-3 rounded-lg border border-dashed border-border-subtle bg-surface-1/50 text-center',
        s.wrapper,
      )}
    >
      <div
        className={cn(
          'flex items-center justify-center rounded-full bg-surface-2 text-text-tertiary',
          s.iconBox,
        )}
      >
        {icon ? (
          <span className={cn('inline-flex', s.icon)}>{icon}</span>
        ) : (
          <Inbox className={s.icon} />
        )}
      </div>

      <div className="space-y-1 max-w-xs">
        <p className={cn('font-semibold text-text-secondary', s.title)}>{title}</p>
        {description && (
          <p className={cn('text-text-tertiary', s.description)}>{description}</p>
        )}
      </div>

      {action && <div className="mt-1">{action}</div>}
    </div>
  );
}
